import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useAnimatedScroll } from "@faire/retailer-visitor-shared/components/AnimatedBanner/useAnimatedScroll";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useSettingImprovedFoiExperience } from "@faire/retailer-visitor-shared/settings/useSettingImprovedFoiExperience";
import { isNotNull } from "@faire/web--source/common/typescriptUtils";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { useIntersectionObserver } from "@faire/web--source/ui/hooks/useIntersectionObserver";
import { trackRetailerOnboardingTopPromoBannerView } from "@faire/web-api--source/events/retailerOnboarding/view/topPromoBanner";
import { trackRetailersRotatingTopBannerView } from "@faire/web-api--source/events/retailers/view/rotatingTopBanner";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
var AnimatedBannerBase = function(param) {
    var banners = param.banners, className = param.className, width = param.width, bannerNames = param.bannerNames, faireValuesBannerNames = param.faireValuesBannerNames;
    var bannersList = banners.filter(isNotNull);
    if (!bannersList.length) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(GridSingleRowContainer, {
        "data-test-id": "banners-grid",
        className: className,
        children: [
            bannersList.map(function(banner, i) {
                return /*#__PURE__*/ _jsx(HiddenBannerWrapper, {
                    children: banner
                }, i);
            }),
            /*#__PURE__*/ _jsx(BannersContainer, {
                banners: banners,
                className: className,
                width: width,
                bannerNames: bannerNames,
                faireValuesBannerNames: faireValuesBannerNames
            })
        ]
    });
};
export var AnimatedBanner = observer(AnimatedBannerBase);
var BannersContainer = function(param) {
    var banners = param.banners, width = param.width, bannerNames = param.bannerNames, faireValuesBannerNames = param.faireValuesBannerNames;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var isImprovedFoiExperience = useSettingImprovedFoiExperience();
    var _useAnimatedScroll = useAnimatedScroll(banners), resumeScroll = _useAnimatedScroll.resumeScroll, pauseScroll = _useAnimatedScroll.pauseScroll, isVisible = _useAnimatedScroll.isVisible, currentIndex = _useAnimatedScroll.currentIndex;
    var _useState = _sliced_to_array(useState(false), 2), isInView = _useState[0], setIsInView = _useState[1];
    var bannerContainerRef = useIntersectionObserver(function(e) {
        return setIsInView(e.isIntersecting);
    });
    var bannerViewFlags = useRef(new Set());
    var isFoiInBannerRotation = _to_consumable_array(bannerNames !== null && bannerNames !== void 0 ? bannerNames : []).concat(_to_consumable_array(faireValuesBannerNames !== null && faireValuesBannerNames !== void 0 ? faireValuesBannerNames : [])).includes("FIRST_ORDER_INCENTIVE");
    var shouldShowBlackBackground = isLoggedInRetailer && isFoiInBannerRotation && isImprovedFoiExperience;
    useEffect(function() {
        if (isInView && !bannerViewFlags.current.has(currentIndex)) {
            bannerViewFlags.current.add(currentIndex);
            if (faireValuesBannerNames) {
                var _faireValuesBannerNames_currentIndex;
                // Legacy tracking for faire values banners
                trackRetailersRotatingTopBannerView((_faireValuesBannerNames_currentIndex = faireValuesBannerNames[currentIndex]) !== null && _faireValuesBannerNames_currentIndex !== void 0 ? _faireValuesBannerNames_currentIndex : "");
                var _faireValuesBannerNames_currentIndex1;
                trackRetailerOnboardingTopPromoBannerView((_faireValuesBannerNames_currentIndex1 = faireValuesBannerNames[currentIndex]) !== null && _faireValuesBannerNames_currentIndex1 !== void 0 ? _faireValuesBannerNames_currentIndex1 : "");
            } else if (bannerNames) {
                var _bannerNames_currentIndex;
                trackRetailerOnboardingTopPromoBannerView((_bannerNames_currentIndex = bannerNames[currentIndex]) !== null && _bannerNames_currentIndex !== void 0 ? _bannerNames_currentIndex : "");
            }
        }
    }, [
        faireValuesBannerNames,
        currentIndex,
        isInView,
        bannerNames
    ]);
    return /*#__PURE__*/ _jsx(BannerContainer, {
        $shouldShowBlackBackground: shouldShowBlackBackground,
        onMouseEnter: pauseScroll,
        onMouseLeave: resumeScroll,
        "data-test-id": "banners-container",
        ref: bannerContainerRef,
        children: /*#__PURE__*/ _jsx(AnimatedWrapper, {
            $isVisible: isVisible,
            $width: width,
            "data-test-id": "animated-banners-wrapper",
            children: banners[currentIndex]
        })
    });
};
var fadeIn = keyframes([
    "0%{opacity:0;transform:translateY(100%);}40%{opacity:0;}100%{opacity:1;transform:translateY(0);}"
]);
var fadeOut = keyframes([
    "0%{opacity:1;}100%{opacity:0;}"
]);
var AnimatedWrapper = styled(Flex).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-e9efbcc2-0"
})([
    "",
    " ",
    " height:100%;"
], function(props) {
    return props.$isVisible ? css([
        "transform:translateY(100%);opacity:0;animation:",
        " 500ms forwards;"
    ], fadeIn) : css([
        "opacity:1;animation:",
        " 500ms forwards;"
    ], fadeOut);
}, function(props) {
    return props.$width ? css([
        "width:",
        ";"
    ], props.$width) : null;
});
var GridSingleRowContainer = styled.div.withConfig({
    componentId: "sc-e9efbcc2-1"
})([
    "display:grid;min-height:36px;"
]);
var HiddenBannerWrapper = styled.div.withConfig({
    componentId: "sc-e9efbcc2-2"
})([
    "visibility:hidden;grid-column:1;grid-row:1;height:max-content;width:100%;"
]);
var BannerContainer = styled(Column).attrs({
    justify: "center"
}).withConfig({
    componentId: "sc-e9efbcc2-3"
})([
    "grid-column:1;grid-row:1;",
    ""
], function(param) {
    var $shouldShowBlackBackground = param.$shouldShowBlackBackground;
    return $shouldShowBlackBackground ? css([
        "background-color:",
        ";"
    ], Core.surface.default) : null;
});
